import React, { useState } from 'react';
import { supabase, fetchCurrentUser } from '../services/supabase';
import { X, Mail, Lock, User, AlertTriangle, Loader2 } from 'lucide-react';

export default function AuthModal({ isOpen, onClose, onAuthSuccess, t, closable = true }) {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (isLogin) {
                const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
                if (signInError) throw signInError;
                const u = await fetchCurrentUser();
                onAuthSuccess(u);
            } else {
                const { data, error: signUpError } = await supabase.auth.signUp({
                    email,
                    password,
                    options: { data: { name } },
                });
                if (signUpError) throw signUpError;

                if (data.session) {
                    // autoconfirm ligado: já entra direto (cai em "aguardando aprovação")
                    const u = await fetchCurrentUser();
                    onAuthSuccess(u);
                } else {
                    // confirmação de e-mail exigida
                    setError('Conta criada! Verifique seu e-mail para confirmar o acesso.');
                    setLoading(false);
                    return;
                }
            }
            onClose();
        } catch (err) {
            console.error('Auth error:', err);
            setError(err.message || 'Erro ao processar autenticação. Verifique os dados.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
            padding: 20,
            cursor: closable ? 'pointer' : 'default'
        }}
            onClick={closable ? onClose : undefined}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: t.bgCard,
                    borderRadius: 2,
                    border: `1.5px solid ${t.ink}`,
                    width: '100%',
                    maxWidth: 400,
                    position: 'relative',
                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
                    overflow: 'hidden'
                }}>
                {/* Header */}
                <div style={{ padding: '24px 24px 0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ fontFamily: t.disp, color: t.text, margin: 0, fontSize: 21, fontWeight: 700, letterSpacing: '-0.01em' }}>
                        {isLogin ? 'Bem-vindo ao Prexo' : 'Criar conta no Prexo'}
                    </h2>
                    {closable && (
                        <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer' }}>
                            <X size={20} />
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit} style={{ padding: 24 }}>
                    {error && (
                        <div style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: `1px solid ${t.danger}`,
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 20,
                            display: 'flex',
                            gap: 8,
                            color: t.danger,
                            fontSize: 13
                        }}>
                            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                            <span>{error}</span>
                        </div>
                    )}

                    {!isLogin && (
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontFamily: t.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textMuted, marginBottom: 6 }}>Nome Completo</label>
                            <div style={{ position: 'relative' }}>
                                <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Seu nome"
                                    required
                                    style={{
                                        width: '100%', background: t.bgInput, border: `1px solid ${t.border}`,
                                        borderRadius: 8, padding: '10px 12px 10px 40px', color: t.text, outline: 'none'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 12, color: t.textMuted, marginBottom: 6 }}>E-mail</label>
                        <div style={{ position: 'relative' }}>
                            <Mail size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="seu@email.com"
                                required
                                style={{
                                    width: '100%', background: t.bgInput, border: `1.5px solid ${t.ink}`,
                                    borderRadius: 0, padding: '11px 12px 11px 40px', color: t.text, outline: 'none', fontFamily: t.mono, fontSize: 14
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: 24 }}>
                        <label style={{ display: 'block', fontSize: 12, color: t.textMuted, marginBottom: 6 }}>Senha</label>
                        <div style={{ position: 'relative' }}>
                            <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                style={{
                                    width: '100%', background: t.bgInput, border: `1.5px solid ${t.ink}`,
                                    borderRadius: 0, padding: '11px 12px 11px 40px', color: t.text, outline: 'none', fontFamily: t.mono, fontSize: 14
                                }}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%', background: t.ink, color: t.bg, border: 'none',
                            borderRadius: 0, padding: '13px', fontFamily: t.mono, fontWeight: 700, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            opacity: loading ? 0.7 : 1
                        }}
                    >
                        {loading ? <Loader2 className="animate-spin" size={20} /> : (isLogin ? 'Entrar' : 'Criar Conta')}
                    </button>

                    <div style={{ marginTop: 20, textAlign: 'center' }}>
                        <button
                            type="button"
                            onClick={() => setIsLogin(!isLogin)}
                            style={{ background: 'none', border: 'none', color: t.accent, cursor: 'pointer', fontSize: 13 }}
                        >
                            {isLogin ? 'Não tem uma conta? Cadastre-se' : 'Já tem uma conta? Entre agora'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
